import api from './api';
import { adminAPI } from './adminApi';

class InventoryAPI {
  // Categories API
  async getCategories(params = {}) {
    const response = await api.get('/inventory/categories', { params });
    return response.data;
  }

  async getCategoryById(id) {
    const response = await api.get(`/inventory/categories/${id}`);
    return response.data;
  }

  async createCategory(categoryData) {
    const response = await api.post('/inventory/categories', categoryData);
    return response.data;
  }

  async updateCategory(id, categoryData) {
    const response = await api.put(`/inventory/categories/${id}`, categoryData);
    return response.data;
  }

  async deleteCategory(id) {
    const response = await api.delete(`/inventory/categories/${id}`);
    return response.data;
  }

  async getCategoryTree() {
    const response = await api.get('/inventory/categories', { params: { tree: true } });
    return response.data;
  }

  async bulkCreateCategories(categoriesData) {
    const response = await api.post('/inventory/categories/bulk', categoriesData);
    return response.data;
  }

  // Items API
  async getItems(params = {}) {
    const response = await api.get('/inventory/items', { params });
    return response.data;
  }

  async getItemsBySupplier(supplierId) {
    const response = await api.get(`/inventory/suppliers/${supplierId}/items`);
    return response.data;
  }

  async getItemById(id) {
    const response = await api.get(`/inventory/items/${id}`);
    return response.data;
  }

  async createItem(itemData) {
    const response = await api.post('/inventory/items', itemData);
    return response.data;
  }

  async updateItem(id, itemData) {
    const response = await api.put(`/inventory/items/${id}`, itemData);
    return response.data;
  }

  async deleteItem(id) {
    const response = await api.delete(`/inventory/items/${id}`);
    return response.data;
  }

  async bulkCreateItems(itemsData) {
    const response = await api.post('/inventory/items/bulk', itemsData);
    return response.data;
  }

  async bulkUpdateItems(itemsData) {
    const response = await api.put('/inventory/items/bulk', itemsData);
    return response.data;
  }

  async searchItems(searchTerm) {
    const response = await api.get('/inventory/items/search', {
      params: { q: searchTerm }
    });
    return response.data;
  }

  async bulkUploadItems(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/inventory/items/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0 // no timeout — waits until all rows are processed and response is returned
    });
    return response.data;
  }

  // Suppliers API
  async getSuppliers(params = {}) {
    const response = await api.get('/inventory/suppliers', { params });
    return response.data;
  }

  async getSupplierById(id) {
    const response = await api.get(`/inventory/suppliers/${id}`);
    return response.data;
  }

  async createSupplier(supplierData) {
    const response = await api.post('/inventory/suppliers', supplierData);
    return response.data;
  }

  async updateSupplier(id, supplierData) {
    const response = await api.put(`/inventory/suppliers/${id}`, supplierData);
    return response.data;
  }

  async deleteSupplier(id) {
    const response = await api.delete(`/inventory/suppliers/${id}`);
    return response.data;
  }

  async retryLedgerAccount(id) {
    const response = await api.post(`/inventory/suppliers/${id}/retry-ledger-account`);
    return response.data;
  }

  async bulkUploadSuppliers(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/inventory/suppliers/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0 // no timeout — waits until all rows are processed and response is returned
    });
    return response.data;
  }

  async bulkUpdateSupplierPhoneNumbers(rows) {
    const response = await api.post('/inventory/suppliers/bulk-update-phone', { rows }, {
      timeout: 0 // no timeout — waits until all rows are processed and response is returned
    });
    return response.data;
  }

  async getSupplierPerformance(id, params = {}) {
    const response = await api.get(`/inventory/suppliers/${id}/performance`, { params });
    return response.data;
  }

  async getSupplierByLedgerAccountId(accountId, ledgerName) {
    const response = await api.get(`/inventory/suppliers/by-ledger/${accountId}`, {
      params: ledgerName ? { name: ledgerName } : {}
    });
    return response.data;
  }

  // Stock API
  async getStockLevels(params = {}) {
    const response = await api.get('/inventory/stock', { params });
    return response.data;
  }

  async getAssetStockLevels(params = {}) {
    const response = await api.get('/inventory/asset-stock', { params });
    return response.data;
  }

  async allocateAssetStock(assetId, data) {
    const response = await api.post(`/inventory/asset-stock/${assetId}/allocate`, data);
    return response.data;
  }

  // Take a disposed / written-off unit out of fixed-asset stock
  async disposeAssetStock(assetId, data) {
    const response = await api.post(`/inventory/asset-stock/${assetId}/dispose`, data);
    return response.data;
  }

  // Put units back into fixed-asset stock when their disposal (asset sale) is cancelled
  async undisposeAssetStock(assetId, data) {
    const response = await api.post(`/inventory/asset-stock/${assetId}/undispose`, data);
    return response.data;
  }

  async deallocateAssetStock(assetId, data) {
    const response = await api.post(`/inventory/asset-stock/${assetId}/deallocate`, data);
    return response.data;
  }

  async getStockByItem(itemId, params = {}) {
    const response = await api.get(`/inventory/stock/item/${itemId}`, { params });
    return response.data;
  }

  async updateStockLevel(stockData) {
    const response = await api.put('/inventory/stock/adjust', stockData);
    return response.data;
  }

  // Direct stock adjustment (without approval) - use adjustStockLevel instead
  async adjustStockLevel(stockLevelId, adjustmentData) {
    const response = await api.post(`/inventory/stock/${stockLevelId}/adjust`, adjustmentData);
    return response.data;
  }

  async transferStock(transferData) {
    const response = await api.post('/inventory/stock/transfer', transferData);
    return response.data;
  }

  async reserveStock(reservationData) {
    const response = await api.post('/inventory/stock/reserve', reservationData);
    return response.data;
  }

  async releaseReservation(reservationId) {
    const response = await api.delete(`/inventory/stock/reserve/${reservationId}`);
    return response.data;
  }

  async getLowStockItems(params = {}) {
    const response = await api.get('/inventory/stock/low-stock', { params });
    return response.data;
  }

  async getOutOfStockItems(params = {}) {
    const response = await api.get('/inventory/stock/out-of-stock', { params });
    return response.data;
  }

  async getStockAvailabilityByWarehouse(warehouse, items = null) {
    const params = { warehouse };
    if (items) {
      params.items = Array.isArray(items) ? items.join(',') : items;
    }
    const response = await api.get('/inventory/stock/availability', { params });
    return response.data;
  }

  // Stock Movements API
  async getStockMovements(params = {}) {
    const response = await api.get('/inventory/stock/movements', { params });
    return response.data;
  }

  async getStockMovementById(id) {
    const response = await api.get(`/inventory/stock/movements/${id}`);
    return response.data;
  }

  async getItemStockHistory(itemId, params = {}) {
    const response = await api.get(`/inventory/stock/items/${itemId}/history`, { params });
    return response.data;
  }

  // Movements API
  async getMovements(params = {}) {
    const response = await api.get('/inventory/movements', { params });
    return response.data;
  }

  async getMovementById(id) {
    const response = await api.get(`/inventory/movements/${id}`);
    return response.data;
  }

  async createMovement(movementData) {
    const response = await api.post('/inventory/movements', movementData);
    return response.data;
  }

  async reverseMovement(id, reversalData) {
    const response = await api.post(`/inventory/movements/${id}/reverse`, reversalData);
    return response.data;
  }

  async bulkCreateMovements(movementsData) {
    const response = await api.post('/inventory/movements/bulk', movementsData);
    return response.data;
  }

  // Assets API
  async getAssets(params = {}) {
    const response = await api.get('/inventory/assets', { params });
    return response.data;
  }

  async getAssetById(id) {
    const response = await api.get(`/inventory/assets/${id}`);
    return response.data;
  }

  async createAsset(assetData) {
    const response = await api.post('/inventory/assets', assetData);
    return response.data;
  }

  async updateAsset(id, assetData) {
    const response = await api.put(`/inventory/assets/${id}`, assetData);
    return response.data;
  }

  async deleteAsset(id) {
    const response = await api.delete(`/inventory/assets/${id}`);
    return response.data;
  }

  async updateAssetLocation(id, locationData) {
    const response = await api.put(`/inventory/assets/${id}/location`, locationData);
    return response.data;
  }

  async assignAsset(id, assignmentData) {
    const response = await api.post(`/inventory/assets/${id}/assign`, assignmentData);
    return response.data;
  }

  async unassignAsset(id) {
    const response = await api.post(`/inventory/assets/${id}/unassign`);
    return response.data;
  }

  async scheduleAssetMaintenance(id, maintenanceData) {
    const response = await api.put(`/inventory/assets/${id}/maintenance/schedule`, maintenanceData);
    return response.data;
  }

  async recordMaintenanceCompletion(id, completionData) {
    const response = await api.post(`/inventory/assets/${id}/maintenance/complete`, completionData);
    return response.data;
  }

  async getAssetReports(reportType, params = {}) {
    const response = await api.get(`/inventory/assets/reports/${reportType}`, { params });
    return response.data;
  }

  // Reports API
  async getInventoryReport(params = {}) {
    const response = await api.get('/inventory/reports/inventory', { params });
    return response.data;
  }

  async getStockLevelsReport(params = {}) {
    const response = await api.get('/inventory/reports/stock-levels', { params });
    return response.data;
  }

  async getStockLevelsReportCSV(params = {}) {
    const response = await api.get('/inventory/reports/stock-levels', {
      params: { ...params, format: 'csv' },
      responseType: 'blob'
    });
    return response;
  }

  async getMovementsReport(params = {}) {
    const response = await api.get('/inventory/reports/movements', { params });
    return response.data;
  }

  // Stock operations: whole flow per request (GTR → GIN → Transfer In / MRN → Consumption)
  async getStockOperationsTransfers(params = {}) {
    const response = await api.get('/inventory/reports/stock-operations/transfers', { params });
    return response.data;
  }

  // Vendor History (Finance → Reports): documents per vendor with a running balance
  async getVendorHistory(params = {}) {
    const response = await api.get('/inventory/reports/vendor-history', { params });
    return response.data;
  }

  // Accounts Payable aging (Finance → Reports): trade GRNs and expense bills, by vendor type
  async getPayablesAging(params = {}) {
    const response = await api.get('/inventory/reports/payables-aging', { params });
    return response.data;
  }

  async getStockOperationsMaterialRequests(params = {}) {
    const response = await api.get('/inventory/reports/stock-operations/material-requests', { params });
    return response.data;
  }

  async getMovementsReportCSV(params = {}) {
    const response = await api.get('/inventory/reports/movements', {
      params: { ...params, format: 'csv' },
      responseType: 'blob'
    });
    return response;
  }

  async getStockAgingReport(params = {}) {
    const response = await api.get('/inventory/reports/aging', { params });
    return response.data;
  }

  async getStockAgingReportCSV(params = {}) {
    const response = await api.get('/inventory/reports/aging', {
      params: { ...params, format: 'csv' },
      responseType: 'blob'
    });
    return response;
  }

  // Reports in the client's layouts (all take productGroup / productType / product)
  async getReorderReport(params = {}) {
    const response = await api.get('/inventory/reports/reorder', { params });
    return response.data;
  }

  async getStockMovementSummary(params = {}) {
    const response = await api.get('/inventory/reports/stock-movement-summary', { params });
    return response.data;
  }

  async getStockValuePeriod(params = {}) {
    const response = await api.get('/inventory/reports/stock-value', { params });
    return response.data;
  }

  async getStockAgeAnalysis(params = {}) {
    const response = await api.get('/inventory/reports/stock-age', { params });
    return response.data;
  }

  async getConsumptionReport(params = {}) {
    const response = await api.get('/inventory/reports/consumption', { params });
    return response.data;
  }

  async getPriceListReport(params = {}) {
    const response = await api.get('/inventory/reports/price-list', { params });
    return response.data;
  }

  // Price levels and item prices per level
  async getPriceLevels(params = {}) {
    const response = await api.get('/inventory/pricing/levels', { params });
    return response.data;
  }

  async createPriceLevel(data) {
    const response = await api.post('/inventory/pricing/levels', data);
    return response.data;
  }

  async updatePriceLevel(id, data) {
    const response = await api.put(`/inventory/pricing/levels/${id}`, data);
    return response.data;
  }

  async getItemPrices(params = {}) {
    const response = await api.get('/inventory/pricing/item-prices', { params });
    return response.data;
  }

  async setItemPrice(data) {
    const response = await api.put('/inventory/pricing/item-prices', data);
    return response.data;
  }

  async getValuationReport(params = {}) {
    const response = await api.get('/inventory/reports/valuation', { params });
    return response.data;
  }

  async getValuationReportCSV(params = {}) {
    const response = await api.get('/inventory/reports/valuation', {
      params: { ...params, format: 'csv' },
      responseType: 'blob'
    });
    return response;
  }

  async getAdvancedAnalytics(params = {}) {
    const response = await api.get('/inventory/reports/analytics', { params });
    return response.data;
  }

  async getDashboardData(params = {}) {
    const response = await api.get('/inventory/reports/dashboard', { params });
    return response.data;
  }

  async generateCustomReport(reportData) {
    const response = await api.post('/inventory/reports/custom', reportData);
    return response.data;
  }

  async getBincardReport(params = {}) {
    const response = await api.get('/inventory/reports/bincard', { params });
    return response.data;
  }

  async getBincardReportCSV(params = {}) {
    const response = await api.get('/inventory/reports/bincard', { 
      params: { ...params, format: 'csv' },
      responseType: 'blob'
    });
    return response;
  }

  // Audits API
  async getAudits(params = {}) {
    const response = await api.get('/inventory/audits', { params });
    return response.data;
  }

  async getAuditById(id) {
    const response = await api.get(`/inventory/audits/${id}`);
    return response.data;
  }

  async createAudit(auditData) {
    const response = await api.post('/inventory/audits', auditData);
    return response.data;
  }

  async updateAudit(id, auditData) {
    const response = await api.put(`/inventory/audits/${id}`, auditData);
    return response.data;
  }

  async startAudit(id) {
    const response = await api.post(`/inventory/audits/${id}/start`);
    return response.data;
  }

  async completeAudit(id) {
    const response = await api.post(`/inventory/audits/${id}/complete`);
    return response.data;
  }

  async generateAuditItems(id, data = {}) {
    const response = await api.post(`/inventory/audits/${id}/generate-items`, data);
    return response.data;
  }

  async recordItemCount(auditId, itemId, countData) {
    const response = await api.post(`/inventory/audits/${auditId}/items/${itemId}/count`, countData);
    return response.data;
  }

  async verifyItemCount(auditId, itemId, verificationData) {
    const response = await api.post(`/inventory/audits/${auditId}/items/${itemId}/verify`, verificationData);
    return response.data;
  }

  async processVarianceInvestigation(auditId, itemId, investigationData) {
    const response = await api.post(`/inventory/audits/${auditId}/items/${itemId}/investigate`, investigationData);
    return response.data;
  }

  async approveStockAdjustment(auditId, itemId, adjustmentData) {
    const response = await api.post(`/inventory/audits/${auditId}/items/${itemId}/approve-adjustment`, adjustmentData);
    return response.data;
  }

  // Stock Adjustment APIs (dedicated table)
  async getStockAdjustments(params = {}) {
    const response = await api.get('/inventory/stock-adjustments', { params });
    return response.data;
  }

  async getPendingStockAdjustments(params = {}) {
    const response = await api.get('/inventory/stock-adjustments/pending', { params });
    return response.data;
  }

  async getStockAdjustmentById(id) {
    const response = await api.get(`/inventory/stock-adjustments/${id}`);
    return response.data;
  }

  async createStockAdjustment(data) {
    const response = await api.post('/inventory/stock-adjustments', data);
    return response.data;
  }

  async updateStockAdjustment(id, data) {
    const response = await api.put(`/inventory/stock-adjustments/${id}`, data);
    return response.data;
  }

  async approveStockAdjustmentById(id, data = {}) {
    const response = await api.post(`/inventory/stock-adjustments/${id}/approve`, data);
    return response.data;
  }

  async rejectStockAdjustmentById(id, data = {}) {
    const response = await api.post(`/inventory/stock-adjustments/${id}/reject`, data);
    return response.data;
  }

  async cancelStockAdjustment(id, data = {}) {
    const response = await api.post(`/inventory/stock-adjustments/${id}/cancel`, data);
    return response.data;
  }

  async getItemAdjustmentHistory(itemId, params = {}) {
    const response = await api.get(`/inventory/stock-adjustments/item/${itemId}/history`, { params });
    return response.data;
  }

  // Stock Movement Approval APIs (legacy)
  async approveMovement(movementId, data = {}) {
    const response = await api.post(`/inventory/movements/${movementId}/approve`, {
      approved: true,
      ...data
    });
    return response.data;
  }

  async rejectMovement(movementId, data = {}) {
    const response = await api.post(`/inventory/movements/${movementId}/approve`, {
      approved: false,
      ...data
    });
    return response.data;
  }

  async getPendingMovements(params = {}) {
    const response = await api.get('/inventory/movements/pending', { params });
    return response.data;
  }

  async getVarianceReport(auditId, params = {}) {
    const response = await api.get(`/inventory/audits/${auditId}/variance-report`, { params });
    return response.data;
  }

  async getComplianceReport(auditId) {
    const response = await api.get(`/inventory/audits/${auditId}/compliance-report`);
    return response.data;
  }

  async submitAuditForReview(id) {
    const response = await api.post(`/inventory/audits/${id}/submit`);
    return response.data;
  }

  async approveAudit(id, approvalData) {
    const response = await api.post(`/inventory/audits/${id}/approve`, approvalData);
    return response.data;
  }

  // Good Received Notes (GRN) API
  async getGRNs(params = {}) {
    const response = await api.get('/inventory/grn', { params });
    return response.data;
  }

  async getGRNById(id) {
    const response = await api.get(`/inventory/grn/${id}`);
    return response.data;
  }

  // Alias for consistency
  async getGoodReceivedNote(id) {
    return this.getGRNById(id);
  }

  async createGRN(grnData) {
    const response = await api.post('/inventory/grn', grnData);
    return response.data;
  }

  async createGRNFromPO(purchaseOrderId, options = {}) {
    const response = await api.post(`/inventory/grn/from-po/${purchaseOrderId}`, options);
    return response.data;
  }

  async getSuppliersWithPendingPOs() {
    const response = await api.get('/inventory/grn/suppliers/pending-pos');
    return response.data;
  }

  async getPendingPOsBySupplier(supplierId) {
    const response = await api.get(`/inventory/grn/suppliers/${supplierId}/pending-pos`);
    return response.data;
  }

  // Fixed-asset GRN: (re)create its Asset Register records (one per accepted unit)
  async registerGRNAssets(id, options = {}) {
    const response = await api.post(`/inventory/grn/${id}/register-assets`, options);
    return response.data;
  }

  async updateGRN(id, grnData) {
    const response = await api.put(`/inventory/grn/${id}`, grnData);
    return response.data;
  }

  async updateGRNStatus(id, status, comments = '') {
    const response = await api.patch(`/inventory/grn/${id}/status`, { status, comments });
    return response.data;
  }

  async partialUpdateGRN(id, updateData) {
    const response = await api.patch(`/inventory/grn/${id}`, updateData);
    return response.data;
  }

  async deleteGRN(id) {
    const response = await api.delete(`/inventory/grn/${id}`);
    return response.data;
  }

  async completeGRNInspection(id, inspectionData) {
    const response = await api.post(`/inventory/grn/${id}/inspection/complete`, inspectionData);
    return response.data;
  }

  async approveGRN(id, comments = '') {
    const response = await api.post(`/inventory/grn/${id}/approve`, { comments });
    return response.data;
  }

  async getGRNReport(id, data) {
    const response = await api.post(`/inventory/document-reports/grn/${id}`, data);
    return response.data;
  }

  async rejectGRN(id, reason = '') {
    const response = await api.post(`/inventory/grn/${id}/reject`, { reason });
    return response.data;
  }

  async returnGRNGoods(id, returnData) {
    const response = await api.post(`/inventory/grn/${id}/return`, returnData);
    return response.data;
  }

  async addGRNComment(id, commentData) {
    const response = await api.post(`/inventory/grn/${id}/comments`, commentData);
    return response.data;
  }

  async getPendingInspections(warehouseId = null) {
    const params = warehouseId ? { warehouse: warehouseId } : {};
    const response = await api.get('/inventory/grn/inspections/pending', { params });
    return response.data;
  }

  async getOverdueInspections(days = 3, warehouseId = null) {
    const params = { days };
    if (warehouseId) params.warehouse = warehouseId;
    const response = await api.get('/inventory/grn/inspections/overdue', { params });
    return response.data;
  }

  // Removed duplicate - using the correct endpoint at the end of the file

  async getGRNSummary(params = {}) {
    const response = await api.get('/inventory/grn/summary/statistics', { params });
    return response.data;
  }

  // Purchase Orders API
  async getPurchaseOrders(params = {}) {
    const response = await api.get('/inventory/purchase-orders', { params });
    return response.data;
  }

  async getPurchaseOrderById(id) {
    const response = await api.get(`/inventory/purchase-orders/${id}`);
    return response.data;
  }

  async createPurchaseOrder(orderData) {
    const response = await api.post('/inventory/purchase-orders', orderData);
    return response.data;
  }

  async updatePurchaseOrder(id, orderData) {
    const response = await api.put(`/inventory/purchase-orders/${id}`, orderData);
    return response.data;
  }

  async deletePurchaseOrder(id) {
    const response = await api.delete(`/inventory/purchase-orders/${id}`);
    return response.data;
  }

  async approvePurchaseOrder(id, approvalData) {
    const response = await api.post(`/inventory/purchase-orders/${id}/approve`, approvalData);
    return response.data;
  }

  async getPurchaseOrderReport(id, data) {
    const response = await api.post(`/inventory/document-reports/purchase-orders/${id}`, data);
    return response.data;
  }

  async sendPurchaseOrder(id, sendData) {
    const response = await api.post(`/inventory/purchase-orders/${id}/send`, sendData);
    return response.data;
  }

  async receivePurchaseOrder(id, receiptData) {
    const response = await api.post(`/inventory/purchase-orders/${id}/receive`, receiptData);
    return response.data;
  }

  async cancelPurchaseOrder(id, cancellationData) {
    const response = await api.post(`/inventory/purchase-orders/${id}/cancel`, cancellationData);
    return response.data;
  }

  async waveOffBalanceQuantity(id) {
    const response = await api.post(`/inventory/purchase-orders/${id}/wave-off`);
    return response.data;
  }

  async getPurchaseOrderStatistics(params = {}) {
    const response = await api.get('/inventory/purchase-orders/statistics', { params });
    return response.data;
  }

  // Purchase Order Approval Levels (Inventory > Settings)
  // NOTE: these return the { success, data } envelope, not the raw axios response.
  async getPOApprovalSettings() {
    const response = await api.get('/inventory/purchase-order-approval-settings');
    return response.data;
  }

  async updatePOApprovalSettings(settingsData) {
    const response = await api.put('/inventory/purchase-order-approval-settings', settingsData);
    return response.data;
  }

  async resolvePOApprovalTier(amount) {
    const response = await api.get('/inventory/purchase-order-approval-settings/resolve', {
      params: { amount }
    });
    return response.data;
  }

  // Supplier Payments API
  async getSupplierPayments(params = {}) {
    const response = await api.get('/inventory/supplier-payments', { params });
    return response.data;
  }

  async getSupplierPaymentById(id) {
    const response = await api.get(`/inventory/supplier-payments/${id}`);
    return response.data;
  }

  async getUnpaidGRNsBySupplier(supplierId) {
    const response = await api.get(`/inventory/supplier-payments/suppliers/${supplierId}/unpaid-grns`);
    return response.data;
  }

  async getAvailableGRNsForPayment(supplierId, params = {}) {
    const response = await api.get(`/inventory/supplier-payments/suppliers/${supplierId}/available-grns`, { params });
    return response.data;
  }

  // Alias for consistency with our component code
  async getSupplierPayment(id) {
    return this.getSupplierPaymentById(id);
  }

  async createSupplierPayment(paymentData) {
    const response = await api.post('/inventory/supplier-payments/create', paymentData);
    return response.data;
  }

  async updateSupplierPayment(id, paymentData) {
    const response = await api.put(`/inventory/supplier-payments/${id}`, paymentData);
    return response.data;
  }

  async deleteSupplierPayment(id) {
    const response = await api.delete(`/inventory/supplier-payments/${id}`);
    return response.data;
  }

  async approveSupplierPayment(id, approvalData) {
    const response = await api.post(`/inventory/supplier-payments/${id}/approve`, approvalData);
    return response.data;
  }

  // Cheque cancelled from the cheque book: reverse the payment / replace the cheque
  async reverseSupplierPaymentCheque(id, chequeNo, data) {
    const response = await api.post(`/inventory/supplier-payments/${id}/cheques/${encodeURIComponent(chequeNo)}/reverse`, data);
    return response.data;
  }

  async replaceSupplierPaymentCheque(id, chequeNo, data) {
    const response = await api.post(`/inventory/supplier-payments/${id}/cheques/${encodeURIComponent(chequeNo)}/replace`, data);
    return response.data;
  }

  async getSupplierPaymentReport(id, data) {
    const response = await api.post(`/inventory/document-reports/supplier-payments/${id}`, data);
    return response.data;
  }

  async rejectSupplierPayment(id, rejectionData) {
    const response = await api.post(`/inventory/supplier-payments/${id}/reject`, rejectionData);
    return response.data;
  }

  async processSupplierPayment(id, processData) {
    const response = await api.post(`/inventory/supplier-payments/${id}/process`, processData);
    return response.data;
  }

  async completeSupplierPayment(id, completionData) {
    const response = await api.post(`/inventory/supplier-payments/${id}/complete`, completionData);
    return response.data;
  }

  async addPartialPayment(id, paymentData) {
    const response = await api.post(`/inventory/supplier-payments/${id}/partial-payments`, paymentData);
    return response.data;
  }

  async addSupplierPaymentComment(id, commentData) {
    const response = await api.post(`/inventory/supplier-payments/${id}/comments`, commentData);
    return response.data;
  }

  async getSupplierPaymentSummary(params = {}) {
    const response = await api.get('/inventory/supplier-payments/summary/statistics', { params });
    return response.data;
  }

  async getOverdueSupplierPayments(params = {}) {
    const response = await api.get('/inventory/supplier-payments/overdue', { params });
    return response.data;
  }

  async getPendingPaymentApprovals(params = {}) {
    const response = await api.get('/inventory/supplier-payments/approvals/pending', { params });
    return response.data;
  }
  
  // ==================== DISTRIBUTION ROUTES API ====================

  async getRoutes(params = {}) {
    const response = await api.get('/inventory/distribution-routes', { params });
    return response.data;
  }

  async getRouteById(id) {
    const response = await api.get(`/inventory/distribution-routes/${id}`);
    return response.data;
  }

  async createRoute(routeData) {
    const response = await api.post('/inventory/distribution-routes', routeData);
    return response.data;
  }

  async updateRoute(id, routeData) {
    const response = await api.put(`/inventory/distribution-routes/${id}`, routeData);
    return response.data;
  }

  async deleteRoute(id) {
    const response = await api.delete(`/inventory/distribution-routes/${id}`);
    return response.data;
  }

  async addStopToRoute(routeId, stopData) {
    const response = await api.post(`/inventory/distribution-routes/${routeId}/stops`, stopData);
    return response.data;
  }

  async optimizeRoute(routeId) {
    const response = await api.post(`/inventory/distribution-routes/${routeId}/optimize`);
    return response.data;
  }

  // ==================== RETURN TYPES API ====================

  async getReturnTypes(params = {}) {
    const response = await api.get('/inventory/return-types', { params });
    return response.data;
  }

  async getReturnTypeById(id) {
    const response = await api.get(`/inventory/return-types/${id}`);
    return response.data;
  }

  async createReturnType(returnTypeData) {
    const response = await api.post('/inventory/return-types', returnTypeData);
    return response.data;
  }

  async updateReturnType(id, returnTypeData) {
    const response = await api.put(`/inventory/return-types/${id}`, returnTypeData);
    return response.data;
  }

  async deleteReturnType(id) {
    const response = await api.delete(`/inventory/return-types/${id}`);
    return response.data;
  }

  async calculateRestockFee(returnTypeId, calculationData) {
    const response = await api.post(`/inventory/return-types/${returnTypeId}/calculate-fee`, calculationData);
    return response.data;
  }

  // ==================== SALES REFS API ====================

  async getSalesRefs(params = {}) {
    const response = await api.get('/inventory/sales-refs', { params });
    return response.data;
  }

  async getSalesRefById(id) {
    const response = await api.get(`/inventory/sales-refs/${id}`);
    return response.data;
  }

  async createSalesRef(salesRefData) {
    const response = await api.post('/inventory/sales-refs', salesRefData);
    return response.data;
  }

  async updateSalesRef(id, salesRefData) {
    const response = await api.put(`/inventory/sales-refs/${id}`, salesRefData);
    return response.data;
  }

  async calculateCommission(salesRefId, calculationData) {
    const response = await api.post(`/inventory/sales-refs/${salesRefId}/calculate-commission`, calculationData);
    return response.data;
  }

  // ==================== PRICING API ====================

  async getPricingLists(params = {}) {
    const response = await api.get('/inventory/pricing', { params });
    return response.data;
  }

  async getPricingListById(id) {
    const response = await api.get(`/inventory/pricing/${id}`);
    return response.data;
  }

  async createPricingList(pricingData) {
    const response = await api.post('/inventory/pricing', pricingData);
    return response.data;
  }

  async updatePricingList(id, pricingData) {
    const response = await api.put(`/inventory/pricing/${id}`, pricingData);
    return response.data;
  }

  async getPrice(pricingListId, priceData) {
    const response = await api.post(`/inventory/pricing/${pricingListId}/get-price`, priceData);
    return response.data;
  }

  // ==================== TAXES API ====================

  async getTaxes(params = {}) {
    const response = await api.get('/inventory/taxes', { params });
    return response.data;
  }

  async getTaxById(id) {
    const response = await api.get(`/inventory/taxes/${id}`);
    return response.data;
  }

  async createTax(taxData) {
    const response = await api.post('/inventory/taxes', taxData);
    return response.data;
  }

  async updateTax(id, taxData) {
    const response = await api.put(`/inventory/taxes/${id}`, taxData);
    return response.data;
  }

  async calculateTax(taxId, calculationData) {
    const response = await api.post(`/inventory/taxes/${taxId}/calculate`, calculationData);
    return response.data;
  }

  // ==================== SALES CHANNELS API ====================

  async getSalesChannels(params = {}) {
    const response = await api.get('/inventory/sales-channels', { params });
    return response.data;
  }

  async getSalesChannelById(id) {
    const response = await api.get(`/inventory/sales-channels/${id}`);
    return response.data;
  }

  async createSalesChannel(channelData) {
    const response = await api.post('/inventory/sales-channels', channelData);
    return response.data;
  }

  async updateSalesChannel(id, channelData) {
    const response = await api.put(`/inventory/sales-channels/${id}`, channelData);
    return response.data;
  }

  async deleteSalesChannel(id) {
    const response = await api.delete(`/inventory/sales-channels/${id}`);
    return response.data;
  }

  // Multiple GRN Payment APIs
  async getMultipleGRNPaymentSummary(supplierIds = []) {
    const params = supplierIds.length > 0 ? { suppliers: supplierIds.join(',') } : {};
    const response = await api.get('/inventory/supplier-payments/multi-grn/summary', { params });
    return response.data;
  }

  async createMultipleGRNPayment(paymentData) {
    const response = await api.post('/inventory/supplier-payments/multi-grn/create', paymentData);
    return response.data;
  }

  async getGRNPaymentAllocations(grnId) {
    const response = await api.get(`/inventory/supplier-payments/grn/${grnId}/allocations`);
    return response.data;
  }

  // Warehouses/Locations API (delegated to admin service)
  async getWarehouses(params = {}) {
    try {
      const response = await adminAPI.getLocations(params);
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouses/locations:', error);
      throw error;
    }
  }

  async getWarehouseById(id) {
    try {
      const response = await adminAPI.getLocationById(id);
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouse/location by ID:', error);
      throw error;
    }
  }

  // ==================== SUPPLIER RETURN NOTES API ====================

  async getSupplierReturnNotes(params = {}) {
    const response = await api.get('/inventory/supplier-return-notes', { params });
    return response.data;
  }

  async getSupplierReturnNoteById(id) {
    const response = await api.get(`/inventory/supplier-return-notes/${id}`);
    return response.data;
  }

  async createSupplierReturnNote(returnNoteData) {
    const response = await api.post('/inventory/supplier-return-notes', returnNoteData);
    return response.data;
  }

  async updateSupplierReturnNote(id, returnNoteData) {
    const response = await api.put(`/inventory/supplier-return-notes/${id}`, returnNoteData);
    return response.data;
  }

  async deleteSupplierReturnNote(id) {
    const response = await api.delete(`/inventory/supplier-return-notes/${id}`);
    return response.data;
  }

  // Workflow operations


  async approveSupplierReturnNote(id, approvalData) {
    const response = await api.post(`/inventory/supplier-return-notes/${id}/approve`, approvalData);
    return response.data;
  }

  async getSupplierReturnNoteReport(id, data) {
    const response = await api.post(`/inventory/document-reports/supplier-return-notes/${id}`, data);
    return response.data;
  }

  async rejectSupplierReturnNote(id, rejectionData) {
    const response = await api.post(`/inventory/supplier-return-notes/${id}/reject`, rejectionData);
    return response.data;
  }

  async processSupplierReturnNote(id, processingData) {
    const response = await api.post(`/inventory/supplier-return-notes/${id}/process`, processingData);
    return response.data;
  }

  async setoffSupplierReturnNote(id, setoffData) {
    const response = await api.post(`/inventory/supplier-return-notes/${id}/setoff`, setoffData);
    return response.data;
  }

  // Helper methods
  // Approved advance / on-account payments of a supplier with an open balance
  async getSupplierAdvancesForSetoff(supplierId) {
    const response = await api.get(`/inventory/supplier-payments/suppliers/${supplierId}/advances`);
    return response.data;
  }

  async getReturnNotesForSetoff(supplierId) {
    const response = await api.get(`/inventory/supplier-return-notes/suppliers/${supplierId}/for-setoff`);
    return response.data;
  }

  async addSupplierReturnNoteComment(id, commentData) {
    const response = await api.post(`/inventory/supplier-return-notes/${id}/comments`, commentData);
    return response.data;
  }

  // ==========================================================================
  // DEBIT NOTES
  // ==========================================================================

  async getDebitNotes(params = {}) {
    const response = await api.get('/inventory/debit-notes', { params });
    return response.data;
  }

  async getDebitNoteById(id) {
    const response = await api.get(`/inventory/debit-notes/${id}`);
    return response.data;
  }

  async createDebitNote(debitNoteData) {
    const response = await api.post('/inventory/debit-notes', debitNoteData);
    return response.data;
  }

  async updateDebitNote(id, debitNoteData) {
    const response = await api.put(`/inventory/debit-notes/${id}`, debitNoteData);
    return response.data;
  }

  async deleteDebitNote(id) {
    const response = await api.delete(`/inventory/debit-notes/${id}`);
    return response.data;
  }

  async approveDebitNote(id, approvalData = {}) {
    const response = await api.post(`/inventory/debit-notes/${id}/approve`, approvalData);
    return response.data;
  }

  async getDebitNoteReport(id, data) {
    const response = await api.post(`/inventory/document-reports/debit-notes/${id}`, data);
    return response.data;
  }

  async rejectDebitNote(id, rejectionData) {
    const response = await api.post(`/inventory/debit-notes/${id}/reject`, rejectionData);
    return response.data;
  }

  async cancelDebitNote(id, data = {}) {
    const response = await api.post(`/inventory/debit-notes/${id}/cancel`, data);
    return response.data;
  }

  // Debit notes available to set off against a supplier payment
  async getDebitNotesForSetoff(supplierId) {
    const response = await api.get(`/inventory/debit-notes/suppliers/${supplierId}/for-setoff`);
    return response.data;
  }

  // GRNs available for building a debit note (quantity = still open; excludeDebitNoteId when editing)
  // Default credit accounts for penalty / rebate debit notes → { PENALTY: { accountId, label }, REBATE: {...} }
  async getDebitNoteChargeDefaults() {
    const response = await api.get('/inventory/debit-notes/charge-defaults');
    return response.data;
  }

  // Bulk entry: many penalty / rebate notes, one per row ({ debitDate, costCenter, notes: [...] })
  async bulkCreateDebitNotes(data) {
    const response = await api.post('/inventory/debit-notes/bulk', data);
    return response.data;
  }

  // GRNs / service completions of all suppliers with something open ({ supplierId, from, to, procurementType, search })
  async getOpenGRNsForDebitNotes(params = {}) {
    const response = await api.get('/inventory/debit-notes/grns/open', { params });
    return response.data;
  }

  // One note per supplier for selected GRNs by a rule ({ grnIds, rule, debitDate, dryRun })
  async bulkDebitNotesFromGRNs(data) {
    const response = await api.post('/inventory/debit-notes/bulk-from-grns', data);
    return response.data;
  }

  async getAvailableGRNsForDebitNote(supplierId, excludeDebitNoteId) {
    const response = await api.get(`/inventory/debit-notes/grns/available/${supplierId}`, {
      params: excludeDebitNoteId ? { excludeDebitNoteId } : {}
    });
    return response.data;
  }

  // Approved supplier returns with no debit note yet ({ supplierId, search })
  async getSupplierReturnsForDebitNote(params = {}) {
    const response = await api.get('/inventory/debit-notes/supplier-returns/available', { params });
    return response.data;
  }

  // Raise one debit note per selected supplier return → { created: [], failed: [] }
  async createDebitNotesFromReturns(returnIds) {
    const response = await api.post('/inventory/debit-notes/from-supplier-returns', { returnIds });
    return response.data;
  }

  // Post the GL entry of an approved debit note whose posting failed
  async repostDebitNoteGl(id) {
    const response = await api.post(`/inventory/debit-notes/${id}/repost-gl`);
    return response.data;
  }

  async getSupplierReturnNoteStatistics(params = {}) {
    const response = await api.get('/inventory/supplier-return-notes/statistics', { params });
    return response.data;
  }

  // Get GRNs available for returns
  async getGRNsAvailableForReturns(supplierId) {
    const response = await api.get(`/inventory/supplier-return-notes/grns/available/${supplierId}`);
    return response.data;
  }

  // ============================================================================
  // PURCHASE REQUESTS API
  // ============================================================================

  // Get all purchase requests with filtering and pagination
  async getPurchaseRequests(params = {}) {
    const response = await api.get('/inventory/purchase-requests', { params });
    return response.data;
  }

  // ============================================================================
  // SERVICES (catalog used on service purchase requests / orders / completions)
  // ============================================================================

  async getServices(params = {}) {
    const response = await api.get('/inventory/services', { params });
    return response.data;
  }

  // { serviceId: { accountId, label, source: 'service' | 'mapping' } | null } — the ledger a service is budgeted to
  async getServiceExpenseAccounts(serviceIds = []) {
    const response = await api.get('/inventory/services/expense-accounts', { params: { ids: serviceIds.join(',') } });
    return response.data;
  }

  async createService(data) {
    const response = await api.post('/inventory/services', data);
    return response.data;
  }

  async updateService(id, data) {
    const response = await api.put(`/inventory/services/${id}`, data);
    return response.data;
  }

  async toggleServiceStatus(id) {
    const response = await api.patch(`/inventory/services/${id}/toggle-status`);
    return response.data;
  }

  async deleteService(id) {
    const response = await api.delete(`/inventory/services/${id}`);
    return response.data;
  }

  // ============================================================================
  // SUPPLIER QUOTATIONS (per approved purchase request)
  // ============================================================================

  async getSupplierQuotations(params = {}) {
    const response = await api.get('/inventory/supplier-quotations', { params });
    return response.data;
  }

  // Purchase requests with their quotations, paged per request; stage = no_quotes | collecting | ready | po_raised
  async getSupplierQuotationsByRequest(params = {}) {
    const response = await api.get('/inventory/supplier-quotations/by-request', { params });
    return response.data;
  }

  async getSupplierQuotationById(id) {
    const response = await api.get(`/inventory/supplier-quotations/${id}`);
    return response.data;
  }

  async createSupplierQuotation(data) {
    const response = await api.post('/inventory/supplier-quotations', data);
    return response.data;
  }

  async updateSupplierQuotation(id, data) {
    const response = await api.put(`/inventory/supplier-quotations/${id}`, data);
    return response.data;
  }

  async deleteSupplierQuotation(id) {
    const response = await api.delete(`/inventory/supplier-quotations/${id}`);
    return response.data;
  }

  async selectSupplierQuotation(id, reason = '') {
    const response = await api.post(`/inventory/supplier-quotations/${id}/select`, { reason });
    return response.data;
  }

  async unselectSupplierQuotation(id) {
    const response = await api.post(`/inventory/supplier-quotations/${id}/unselect`);
    return response.data;
  }

  // PR lines × quotations, lowest per line / overall flagged
  async compareSupplierQuotations(prId) {
    const response = await api.get(`/inventory/supplier-quotations/compare/${prId}`);
    return response.data;
  }

  // Get purchase request by ID
  async getPurchaseRequestById(id) {
    const response = await api.get(`/inventory/purchase-requests/${id}`);
    return response.data;
  }

  // Create new purchase request
  async createPurchaseRequest(prData) {
    const response = await api.post('/inventory/purchase-requests', prData);
    return response.data;
  }

  // Update purchase request
  async updatePurchaseRequest(id, prData) {
    const response = await api.put(`/inventory/purchase-requests/${id}`, prData);
    return response.data;
  }

  // Delete purchase request (soft delete)
  async deletePurchaseRequest(id) {
    const response = await api.delete(`/inventory/purchase-requests/${id}`);
    return response.data;
  }

  // Submit purchase request for approval
  async submitPurchaseRequestForApproval(id) {
    const response = await api.post(`/inventory/purchase-requests/${id}/submit`);
    return response.data;
  }

  // Approve purchase request
  async approvePurchaseRequest(id, approvalData = {}) {
    const response = await api.post(`/inventory/purchase-requests/${id}/approve`, approvalData);
    return response.data;
  }

  async getPurchaseRequestReport(id, data) {
    const response = await api.post(`/inventory/document-reports/purchase-requests/${id}`, data);
    return response.data;
  }

  // Reject purchase request
  async rejectPurchaseRequest(id, rejectionData) {
    const response = await api.post(`/inventory/purchase-requests/${id}/reject`, rejectionData);
    return response.data;
  }

  // Convert purchase request to purchase order
  async convertPurchaseRequestToPO(id, conversionData) {
    const response = await api.post(`/inventory/purchase-requests/${id}/convert-to-po`, conversionData);
    return response.data;
  }

  // Add internal note to purchase request
  async addPurchaseRequestNote(id, noteData) {
    const response = await api.post(`/inventory/purchase-requests/${id}/notes`, noteData);
    return response.data;
  }

  // Upload attachment to purchase request
  async uploadPurchaseRequestAttachment(id, attachmentData) {
    const response = await api.post(`/inventory/purchase-requests/${id}/attachments`, attachmentData);
    return response.data;
  }

  // Get pending approvals for current user
  async getPendingPurchaseRequestApprovals() {
    const response = await api.get('/inventory/purchase-requests/pending-approvals');
    return response.data;
  }

  // Get overdue purchase requests
  async getOverduePurchaseRequests() {
    const response = await api.get('/inventory/purchase-requests/overdue');
    return response.data;
  }

  // Get purchase request summary/dashboard data
  async getPurchaseRequestSummary(params = {}) {
    const response = await api.get('/inventory/purchase-requests/summary', { params });
    return response.data;
  }

  // Bulk operations on purchase requests
  async bulkPurchaseRequestOperations(operationData) {
    const response = await api.post('/inventory/purchase-requests/bulk', operationData);
    return response.data;
  }

  // ============================================================================
  // GOODS TRANSFER REQUESTS API
  // ============================================================================

  // Get all goods transfer requests
  async getGoodsTransferRequests(params = {}) {
    const response = await api.get('/inventory/stock-transfers', { params });
    return response.data;
  }

  // Get goods transfer request by ID
  async getGoodsTransferRequestById(id) {
    const response = await api.get(`/inventory/stock-transfers/${id}`);
    return response.data;
  }

  // Create new goods transfer request
  async createGoodsTransferRequest(gtrData) {
    const response = await api.post('/inventory/stock-transfers', gtrData);
    return response.data;
  }

  // Update goods transfer request
  async updateGoodsTransferRequest(id, gtrData) {
    const response = await api.put(`/inventory/stock-transfers/${id}`, gtrData);
    return response.data;
  }

  // Delete goods transfer request
  async deleteGoodsTransferRequest(id) {
    const response = await api.delete(`/inventory/stock-transfers/${id}`);
    return response.data;
  }

  // Submit goods transfer request for approval
  async submitGoodsTransferRequestForApproval(id) {
    const response = await api.post(`/inventory/stock-transfers/${id}/submit`);
    return response.data;
  }

  // Approve goods transfer request
  async approveGoodsTransferRequest(id, approvalData = {}) {
    const response = await api.post(`/inventory/stock-transfers/${id}/approve`, approvalData);
    return response.data;
  }

  // Reject goods transfer request
  async rejectGoodsTransferRequest(id, rejectionData) {
    const response = await api.post(`/inventory/stock-transfers/${id}/reject`, rejectionData);
    return response.data;
  }

  // Cancel goods transfer request
  async cancelGoodsTransferRequest(id, reason) {
    const response = await api.post(`/inventory/stock-transfers/${id}/cancel`, { reason });
    return response.data;
  }

  // Get pending goods transfer request approvals
  async getPendingGoodsTransferRequestApprovals() {
    const response = await api.get('/inventory/stock-transfers/pending-approvals');
    return response.data;
  }

  // Get goods transfer request summary
  async getGoodsTransferRequestSummary(params = {}) {
    const response = await api.get('/inventory/stock-transfers/summary', { params });
    return response.data;
  }

  // Add internal note to goods transfer request
  async addGoodsTransferRequestNote(id, noteData) {
    const response = await api.post(`/inventory/stock-transfers/${id}/notes`, noteData);
    return response.data;
  }

  // Update transfer progress for a line item
  async updateGoodsTransferProgress(id, lineItemId, transferredQuantity) {
    const response = await api.put(`/inventory/stock-transfers/${id}/line-items/${lineItemId}/progress`, {
      transferredQuantity
    });
    return response.data;
  }

  // Get stock movements for a specific GTR
  async getGTRStockMovements(id) {
    const response = await api.get(`/inventory/stock-transfers/${id}/stock-movements`);
    return response.data;
  }

  // Count approved GTRs at my location (as source) still awaiting a Goods Issue Note
  async getGTRToIssueCount() {
    const response = await api.get('/inventory/stock-transfers/to-issue-count');
    return response.data;
  }

  // ============================================================================
  // GOODS ISSUE NOTES API
  // ============================================================================

  // Get all goods issue notes (scoped to the caller's location on the backend)
  async getGoodsIssueNotes(params = {}) {
    const response = await api.get('/inventory/goods-issue-notes', { params });
    return response.data;
  }

  // Get a single goods issue note by id
  async getGoodsIssueNoteById(id) {
    const response = await api.get(`/inventory/goods-issue-notes/${id}`);
    return response.data;
  }

  // Create a goods issue note (draft) against an approved GTR — no stock moves yet
  async createGoodsIssueNote(data) {
    const response = await api.post('/inventory/goods-issue-notes', data);
    return response.data;
  }

  // Update a goods issue note — only permitted while still a draft
  async updateGoodsIssueNote(id, data) {
    const response = await api.put(`/inventory/goods-issue-notes/${id}`, data);
    return response.data;
  }

  // Delete a goods issue note — only permitted while still a draft
  async deleteGoodsIssueNote(id) {
    const response = await api.delete(`/inventory/goods-issue-notes/${id}`);
    return response.data;
  }

  // Submit a draft goods issue note for approval
  async submitGoodsIssueNoteForApproval(id) {
    const response = await api.post(`/inventory/goods-issue-notes/${id}/submit`);
    return response.data;
  }

  // Approve a pending goods issue note — this is what executes the actual stock
  // transfer (source decrement + destination increment)
  async approveGoodsIssueNote(id) {
    const response = await api.post(`/inventory/goods-issue-notes/${id}/approve`);
    return response.data;
  }

  // Reject a pending goods issue note — no stock impact
  async rejectGoodsIssueNote(id, reason) {
    const response = await api.post(`/inventory/goods-issue-notes/${id}/reject`, { reason });
    return response.data;
  }

  // ============================================================================
  // MATERIAL REQUEST NOTES API
  // ============================================================================
  // Department material request for inventory items. Flow: create (draft) ->
  // submit (pending) -> release (one or more rounds, spawning inventory consumption
  // records) -> completed.

  async getMaterialRequestNotes(params = {}) {
    const response = await api.get('/inventory/material-request-notes', { params });
    return response.data;
  }

  async getMaterialRequestNoteById(id) {
    const response = await api.get(`/inventory/material-request-notes/${id}`);
    return response.data;
  }

  async createMaterialRequestNote(data) {
    const response = await api.post('/inventory/material-request-notes', data);
    return response.data;
  }

  async updateMaterialRequestNote(id, data) {
    const response = await api.put(`/inventory/material-request-notes/${id}`, data);
    return response.data;
  }

  async deleteMaterialRequestNote(id) {
    const response = await api.delete(`/inventory/material-request-notes/${id}`);
    return response.data;
  }

  async submitMaterialRequestNote(id) {
    const response = await api.post(`/inventory/material-request-notes/${id}/submit`);
    return response.data;
  }

  async rejectMaterialRequestNote(id, reason) {
    const response = await api.post(`/inventory/material-request-notes/${id}/reject`, { reason });
    return response.data;
  }

  // Release materials against a pending/partially-released MRN — records inventory
  // consumption. Record only, no stock mutation.
  async releaseMaterialRequestNote(id, data) {
    const response = await api.post(`/inventory/material-request-notes/${id}/release`, data);
    return response.data;
  }

  // Each MRN line's item expense account (null → a ledger must be chosen on release)
  async getMaterialRequestNoteExpenseAccounts(id) {
    const response = await api.get(`/inventory/material-request-notes/${id}/expense-accounts`);
    return response.data;
  }

  // ============================================================================
  // INVENTORY CONSUMPTION API (read-only)
  // ============================================================================

  async getInventoryConsumptions(params = {}) {
    const response = await api.get('/inventory/inventory-consumption', { params });
    return response.data;
  }

  async getInventoryConsumptionById(id) {
    const response = await api.get(`/inventory/inventory-consumption/${id}`);
    return response.data;
  }

  // ============================================================================
  // TRANSFER IN API
  // ============================================================================
  // Destination-location receiving step for an in-transit Goods Issue Note. Always
  // system-created (when a GIN is approved) — no manual create/delete.

  // Get all transfer-ins (scoped to the caller's location on the backend)
  async getTransferIns(params = {}) {
    const response = await api.get('/inventory/transfer-in', { params });
    return response.data;
  }

  // Get a single transfer-in by id
  async getTransferInById(id) {
    const response = await api.get(`/inventory/transfer-in/${id}`);
    return response.data;
  }

  // Update a pending transfer-in's received quantities/condition/notes before approving
  async updateTransferIn(id, data) {
    const response = await api.put(`/inventory/transfer-in/${id}`, data);
    return response.data;
  }

  // Approve a pending transfer-in — credits destination stock for the received amount
  // and drains the in-transit ledger for the full dispatched amount
  async approveTransferIn(id, data = {}) {
    const response = await api.post(`/inventory/transfer-in/${id}/approve`, data);
    return response.data;
  }

  // Reject a pending transfer-in — reverses the dispatch, returning stock to source
  async rejectTransferIn(id, reason) {
    const response = await api.post(`/inventory/transfer-in/${id}/reject`, { reason });
    return response.data;
  }

}

const inventoryAPIInstance = new InventoryAPI();
export { inventoryAPIInstance as inventoryAPI };
export default inventoryAPIInstance;